import { useMutation, useQuery } from "@tanstack/react-query";

import type { AuthStrategy } from "sandbox/types";

import { extractRestErrorMessage } from "shell/api/utils";

import { sandboxFetch } from "../client";

const throwError = async (response: Response): Promise<never> => {
  const body = await response.json().catch(() => ({}));
  const message = extractRestErrorMessage(body) ?? response.statusText;
  throw Object.assign(new Error(message), { status: response.status, body });
};

export const useSandboxMutation = <TData, TVariables = void>(
  path: string | ((data: TVariables) => string),
  options?: { method?: string; auth?: AuthStrategy },
) =>
  useMutation<TData, unknown, TVariables>({
    mutationFn: async data => {
      const resolvedPath = typeof path === "function" ? path(data) : path;
      const response = await sandboxFetch(resolvedPath, {
        method: options?.method ?? "POST",
        body: data as Record<string, unknown>,
        auth: options?.auth ?? { mode: "no-auth" },
      });
      if (!response.ok) await throwError(response);
      if (response.status === 204) return undefined as TData;
      return response.json() as Promise<TData>;
    },
  });

export const useSandboxQuery = <TData>(
  queryKey: ReadonlyArray<unknown>,
  path: string,
  options?: { auth?: AuthStrategy },
) =>
  useQuery<TData>({
    queryKey,
    queryFn: async () => {
      const response = await sandboxFetch(path, {
        method: "GET",
        auth: options?.auth ?? { mode: "authenticated" },
      });
      if (!response.ok) await throwError(response);
      if (response.status === 204) return undefined as TData;
      return response.json() as Promise<TData>;
    },
  });
